import { useMemo, type ReactElement } from 'react'
import { DateTime } from 'luxon'
import {
  isEmpty,
  type AdvancedDateRangeOffset,
  type DashboardWidget,
  type ReportColumnDefinition,
  type ReportDateRange,
  type ReportDateRangePreset,
  type ReportDateRangeUnit,
  type ReportTimePeriod,
  type TabularResult,
} from '@terros/common'
import { Group, Text } from '@mantine/core'
import { LineChart } from '@mantine/charts'
import {
  getSelectedReportColumnDisplayLabel,
  type ReportColumnVariantGroup,
  useReportColumnsMetadata,
} from '@front/model'
import { toThresholdReferenceLines } from './lineWidgetReferenceLines'

type Props = {
  widget: Extract<DashboardWidget, { type: 'line' }>
  dateRange: ReportDateRange | undefined
  data: TabularResult
}

type ChartRecord = Record<string, string | number | null>

export function LineWidget({ data, dateRange, widget }: Props): ReactElement {
  const { columnsMap: reportColumnsMap, variantMap } = useReportColumnsMetadata()
  const selectedColumns = useMemo(() => new Set(widget.metrics.map((metric) => metric.kpi)), [widget.metrics])

  const chartData = useMemo(() => {
    if (isEmpty(data.rows)) return []
    return buildChartData(data, widget, dateRange, reportColumnsMap, variantMap)
  }, [data, widget, dateRange, reportColumnsMap, variantMap])

  const series = useMemo(
    () =>
      widget.metrics.map((metric) => {
        return {
          name: getSelectedReportColumnDisplayLabel({
            value: metric.kpi,
            selectedColumns,
            columnsMap: reportColumnsMap,
            variantMap,
          }),
          color: getSeriesColor(metric.kpi),
        }
      }),
    [widget.metrics, selectedColumns, reportColumnsMap, variantMap]
  )
  const referenceLines = useMemo(() => toThresholdReferenceLines(widget), [widget])

  if (isEmpty(chartData)) return <EmptyLineWidget />

  return (
    <LineChart
      curveType='linear'
      data={chartData}
      dataKey='date'
      h={200}
      referenceLines={referenceLines}
      series={series}
      tooltipAnimationDuration={200}
      withDots={false}
      withLegend={series.length > 1}
    />
  )
}

function buildChartData(
  data: TabularResult,
  widget: Extract<DashboardWidget, { type: 'line' }>,
  dateRange: ReportDateRange | undefined,
  reportColumnsMap: Map<string, ReportColumnDefinition>,
  variantMap: Map<string, ReportColumnVariantGroup>
): ChartRecord[] {
  const period = toTimePeriod(widget.resolution)
  const selectedColumns = new Set(widget.metrics.map((metric) => metric.kpi))
  const metricNames = widget.metrics.map((metric) => {
    return getSelectedReportColumnDisplayLabel({
      value: metric.kpi,
      selectedColumns,
      columnsMap: reportColumnsMap,
      variantMap,
    })
  })
  const rowByBucket = new Map<string, TabularResult['rows'][number]>()

  data.rows.forEach((row) => {
    const bucket = toBucketStart(row[0], period)
    if (bucket) rowByBucket.set(toBucketKey(bucket), row)
  })

  const buckets = getDateRangeBuckets(dateRange, period)
  if (isEmpty(buckets)) {
    return data.rows.map((row) => rowToChartRecord(row, metricNames, period))
  }

  return buckets.map((bucket) => {
    const row = rowByBucket.get(toBucketKey(bucket))
    if (!row) return emptyChartRecord(bucket, metricNames, period)
    return rowToChartRecord(row, metricNames, period)
  })
}

function rowToChartRecord(
  row: TabularResult['rows'][number],
  metricNames: string[],
  period: ReportTimePeriod
): ChartRecord {
  const bucket = toBucketStart(row[0], period)
  const record: ChartRecord = { date: formatDateLabel(bucket ?? row[0], period) }

  metricNames.forEach((name, metricIndex) => {
    const value = row[metricIndex + 1]
    record[name] = typeof value === 'number' ? value : null
  })
  return record
}

function emptyChartRecord(bucket: DateTime, metricNames: string[], period: ReportTimePeriod): ChartRecord {
  const record: ChartRecord = { date: formatDateLabel(bucket, period) }
  metricNames.forEach((name) => {
    record[name] = 0
  })
  return record
}

function getDateRangeBuckets(dateRange: ReportDateRange | undefined, period: ReportTimePeriod): DateTime[] {
  const bounds = toDateRangeBounds(dateRange)
  if (!bounds) return []

  const buckets: DateTime[] = []
  let cursor = bounds.start.startOf(period)
  const end = bounds.end.startOf(period)

  while (cursor <= end) {
    buckets.push(cursor)
    cursor = cursor.plus({ [period]: 1 })
  }

  return buckets
}

function toDateRangeBounds(dateRange: ReportDateRange | undefined): { start: DateTime; end: DateTime } | undefined {
  if (!dateRange) return toRelativeDateRangeBounds(30, 'days')

  switch (dateRange.type) {
    case 'fixed':
      return { start: toDateTime(dateRange.startTime).startOf('day'), end: toDateTime(dateRange.endTime).endOf('day') }
    case 'relative':
      return toRelativeDateRangeBounds(dateRange.value, dateRange.unit)
    case 'preset':
      return toPresetDateRangeBounds(dateRange.period)
    case 'advanced':
      return toAdvancedDateRangeBounds(dateRange.start, dateRange.end)
  }
}

function toRelativeDateRangeBounds(value: number, unit: ReportDateRangeUnit): { start: DateTime; end: DateTime } {
  const end = DateTime.utc().endOf('day')
  return { start: end.minus({ [unit]: value }).startOf('day'), end }
}

function toPresetDateRangeBounds(period: ReportDateRangePreset): { start: DateTime; end: DateTime } | undefined {
  const now = DateTime.utc()
  switch (period) {
    case 'today':
      return { start: now.startOf('day'), end: now.endOf('day') }
    case 'yesterday':
      return { start: now.minus({ days: 1 }).startOf('day'), end: now.minus({ days: 1 }).endOf('day') }
    case 'this_week':
      return { start: now.startOf('week'), end: now.endOf('day') }
    case 'this_month':
      return { start: now.startOf('month'), end: now.endOf('day') }
    case 'this_quarter':
      return { start: now.startOf('quarter'), end: now.endOf('day') }
    case 'this_year':
    case 'year_to_date':
      return { start: now.startOf('year'), end: now.endOf('day') }
    case 'last_week':
      return { start: now.minus({ weeks: 1 }).startOf('week'), end: now.minus({ weeks: 1 }).endOf('week') }
    case 'last_month':
      return { start: now.minus({ months: 1 }).startOf('month'), end: now.minus({ months: 1 }).endOf('month') }
    case 'last_quarter':
      return {
        start: now.minus({ quarters: 1 }).startOf('quarter'),
        end: now.minus({ quarters: 1 }).endOf('quarter'),
      }
    case 'last_year':
      return { start: now.minus({ years: 1 }).startOf('year'), end: now.minus({ years: 1 }).endOf('year') }
    case 'all_time':
      return undefined
  }
}

function toAdvancedDateRangeBounds(
  start: AdvancedDateRangeOffset | undefined,
  end: AdvancedDateRangeOffset | undefined
): { start: DateTime; end: DateTime } {
  const now = DateTime.utc()
  return {
    start: start
      ? now[start.direction === 'minus' ? 'minus' : 'plus']({ [start.unit]: start.value }).startOf('day')
      : now.startOf('day'),
    end: end
      ? now[end.direction === 'minus' ? 'minus' : 'plus']({ [end.unit]: end.value }).endOf('day')
      : now.endOf('day'),
  }
}

function toTimePeriod(resolution: Extract<DashboardWidget, { type: 'line' }>['resolution']): ReportTimePeriod {
  if (!resolution || resolution === 'auto') return 'day'
  return resolution
}

function toBucketStart(value: unknown, period: ReportTimePeriod): DateTime | undefined {
  const date = toDateTime(value)
  if (!date.isValid) return undefined
  return date.startOf(period)
}

function toBucketKey(value: DateTime): string {
  return value.toUTC().toISO() ?? value.toMillis().toString()
}

function toDateTime(value: unknown): DateTime {
  if (typeof value === 'number') return DateTime.fromMillis(value, { zone: 'utc' })
  if (typeof value === 'string') return DateTime.fromISO(value, { zone: 'utc' })
  if (DateTime.isDateTime(value)) return value.toUTC()
  return DateTime.invalid('Unsupported date value')
}

function formatDateLabel(value: unknown, period: ReportTimePeriod): string {
  const date = toDateTime(value)
  if (!date.isValid) return typeof value === 'string' ? value : ''

  switch (period) {
    case 'hour':
      return date.toFormat('MMM d, h a')
    case 'year':
      return date.toFormat('yyyy')
    case 'month':
      return date.toFormat('MMM yyyy')
    case 'quarter':
      return `Q${date.quarter} ${date.toFormat('yyyy')}`
    case 'week':
    case 'day':
      return date.toFormat('MMM d')
  }
}

function getSeriesColor(kpi: string): string {
  const colors = ['blue.6', 'teal.6', 'orange.6', 'red.6', 'violet.6', 'pink.6']
  let hash = 0
  for (let i = 0; i < kpi.length; i++) {
    hash = kpi.charCodeAt(i) + ((hash << 5) - hash)
  }
  return colors[Math.abs(hash) % colors.length]
}

function EmptyLineWidget(): ReactElement {
  return (
    <Group justify='center'>
      <Text c='dimmed' size='sm'>
        No data
      </Text>
    </Group>
  )
}
